/**
 * Initializes homepage banner carousels that depend on the global Swiper build.
 */
(() => {
  const roots = () => document.querySelectorAll('[data-store-home-banner]');

  const demoteDuplicateLcp = (swiperEl) => {
    swiperEl.querySelectorAll('.swiper-slide-duplicate img').forEach((img) => {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('fetchpriority', 'low');
    });
  };

  const syncPages = (root, swiper) => {
    const index = swiper.realIndex;
    root.querySelectorAll('[data-store-home-banner-page]').forEach((page, i) => {
      page.classList.toggle('is-active', i === index);
    });
  };

  const bindDragClickGuard = (el) => {
    const threshold = 8;
    let pointerDown = false;
    let dragMoved = false;
    let startX = 0;
    let startY = 0;

    el.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerDown = true;
      dragMoved = false;
      startX = event.clientX;
      startY = event.clientY;
    });

    window.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;
      if (
        Math.abs(event.clientX - startX) > threshold ||
        Math.abs(event.clientY - startY) > threshold
      ) {
        dragMoved = true;
      }
    });

    const endPointer = () => {
      if (!pointerDown) return;
      pointerDown = false;
      window.setTimeout(() => {
        dragMoved = false;
      }, 200);
    };

    window.addEventListener('pointerup', endPointer);
    window.addEventListener('pointercancel', endPointer);

    el.addEventListener(
      'click',
      (event) => {
        if (!dragMoved) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        dragMoved = false;
      },
      true
    );

    return threshold;
  };

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.storeHomeBannerReady === 'true') return;

    const el = root.querySelector('[data-store-home-banner-swiper]');
    if (!(el instanceof HTMLElement) || el.swiper) return;
    if (typeof window.Swiper !== 'function') return;

    const threshold = bindDragClickGuard(el);
    const loop = root.dataset.loop === 'true';
    const autoplay = root.dataset.autoplay === 'true';
    const delay = Number(root.dataset.autoplayDelay || '9000');
    const pages = root.querySelectorAll('[data-store-home-banner-page]');
    const slideLabelTemplate = root.dataset.labelSlide || 'Slide __index__ of __total__';

    const swiper = new window.Swiper(el, {
      loop,
      rewind: !loop,
      speed: 600,
      watchSlidesProgress: true,
      threshold,
      preventClicks: true,
      preventClicksPropagation: true,
      autoplay: autoplay
        ? {
            delay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
      navigation: {
        nextEl: root.querySelector('[data-store-home-banner-next]'),
        prevEl: root.querySelector('[data-store-home-banner-prev]'),
      },
      a11y: {
        prevSlideMessage: root.dataset.labelPrev || 'Previous slide',
        nextSlideMessage: root.dataset.labelNext || 'Next slide',
        slideLabelMessage: slideLabelTemplate
          .replace('__index__', '{{index}}')
          .replace('__total__', '{{slidesLength}}'),
      },
      on: {
        init() {
          demoteDuplicateLcp(el);
          syncPages(root, this);
        },
        slideChange() {
          syncPages(root, this);
          demoteDuplicateLcp(el);
        },
      },
    });

    pages.forEach((page) => {
      page.addEventListener('click', () => {
        const index = Number(page.getAttribute('data-index') || '0');
        if (swiper.params.loop && typeof swiper.slideToLoop === 'function') {
          swiper.slideToLoop(index);
        } else {
          swiper.slideTo(index);
        }
      });
    });

    root.dataset.storeHomeBannerReady = 'true';
  };

  const initAll = () => {
    roots().forEach(initRoot);
  };

  const waitForSwiper = (attempt = 0) => {
    if (typeof window.Swiper === 'function') {
      initAll();
      return;
    }
    if (attempt > 40) return;
    window.setTimeout(() => waitForSwiper(attempt + 1), 50);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForSwiper());
  } else {
    waitForSwiper();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!(section instanceof HTMLElement)) return;
    const root = section.querySelector('[data-store-home-banner]');
    if (root) {
      root.dataset.storeHomeBannerReady = 'false';
      waitForSwiper();
    }
  });
})();
